import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BackgroundColor from "../component/BackgroundColor";
import SEOHead from "../component/SEOHead";
import { decryptData } from "../Utils/encryption";
import { toast } from "react-hot-toast";
import { Modal, Box } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StripeCheckoutForm from "../component/StripeCheckoutForm";
import OrderSuccessModal from "../component/OrderSuccessModal";
import { generateInvoice } from "../Utils/invoiceGenerator";
import { useSelector, useDispatch } from "react-redux";
import { fetchCart } from "../Redux/Slice/cart.slice";
import { fetchBundles } from "../Redux/Slice/bundle.slice";
import axiosInstance from "../Utils/axiosInstance";

// Icons
import { FaWindows, FaApple, FaXbox, FaTags } from "react-icons/fa";
import { DiAndroid } from "react-icons/di";
import { SiNintendoswitch, SiPlaystation, SiOculus, SiSteamdeck } from "react-icons/si";
import { TbDeviceVisionProFilled } from "react-icons/tb";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const getPlatformIcon = (platform) => {
  switch (platform) {
    case "windows":
      return <FaWindows className="text-blue-400" />;
    case "ios":
      return <FaApple className="text-white" />;
    case "android":
      return <DiAndroid className="text-green-400" />;
    case "nintendo":
      return <SiNintendoswitch className="text-red-400" />;
    case "quest":
      return <SiOculus className="text-sky-400" />;
    case "playstation":
      return <SiPlaystation className="text-blue-500" />;
    case "xbox":
      return <FaXbox className="text-green-400" />;
    case "visionpro":
      return <TbDeviceVisionProFilled className="text-purple-400" />;
    case "steamdeck":
      return <SiSteamdeck className="text-blue-400" />;
    default:
      return null;
  }
};

export default function Bundles() {
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userId = localStorage.getItem("yoyouserId");
  const token = localStorage.getItem("yoyoToken");
  const user = useSelector((state) => state.auth.user);

  const { bundles, loading } = useSelector((state) => state.bundle);

  // Checkout State
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [openStripeModal, setOpenStripeModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [orderData, setOrderData] = useState(null);

  // For choosing platform for each game in the bundle
  const [platformModalOpen, setPlatformModalOpen] = useState(false);
  const [currentBundle, setCurrentBundle] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState({}); // { gameId: platform }

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(fetchBundles());
  }, [dispatch]);

  const initiateBundlePurchase = (bundle) => {
    if (!userId || !token) {
      navigate("/login");
      return;
    }

    // Initialize default platforms (pick the first available platform for each game)
    const initialPlatforms = {};
    let missingPlatforms = false;

    bundle.games.forEach((g) => {
      const available = Object.keys(g.platforms || {}).filter(
        (key) => g.platforms[key]?.available
      );
      if (available.length > 0) {
        initialPlatforms[g._id] = available[0];
      } else {
        missingPlatforms = true;
      }
    });

    if (missingPlatforms) {
      toast.error("One or more games in this bundle do not have any available platforms.");
      return;
    }

    setSelectedPlatforms(initialPlatforms);
    setCurrentBundle(bundle);
    setPlatformModalOpen(true);
  };

  const handlePlatformChange = (gameId, platform) => {
    setSelectedPlatforms((prev) => ({
      ...prev,
      [gameId]: platform,
    }));
  };

  const handleConfirmPurchase = async () => {
    setPlatformModalOpen(false);
    if (checkoutLoading) return;
    setCheckoutLoading(true);

    const pricePerGame = Number((currentBundle.price / 3).toFixed(2));

    const checkoutItems = currentBundle.games.map((g) => {
      const platform = selectedPlatforms[g._id];
      return {
        game: g._id,
        name: g.title,
        platform,
        price: pricePerGame,
      };
    });

    const subtotal = currentBundle.price;
    const tax = subtotal * 0.18;
    const discount = subtotal * 0.05;
    const totalAmount = Number((subtotal + tax - discount).toFixed(2));

    try {
      if (process.env.REACT_APP_PAYMENT_TYPE === "razorpay") {
        const response = await axiosInstance.post("/order/create", { amount: totalAmount });
        if (response.data.success) {
          const { razorpayOrder } = response.data;
          const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY_ID,
            amount: razorpayOrder.amount,
            currency: razorpayOrder.currency,
            name: "Yoyo Game Bundles",
            description: `Purchase of ${currentBundle.name}`,
            order_id: razorpayOrder.id,
            handler: async function (paymentResponse) {
              try {
                const verifyRes = await axiosInstance.post("/order/verify", {
                  items: checkoutItems,
                  amount: totalAmount,
                  paymentResponse,
                  buyNow: true,
                });
                if (verifyRes.data.success || verifyRes.data.order) {
                  dispatch(fetchCart());
                  toast.success("Bundle purchased successfully!");

                  try {
                    const updatedOrder = verifyRes.data.order;
                    const userDetails = {
                      name: user?.fullName ? decryptData(user.fullName) : "Customer",
                      email: user?.email ? decryptData(user.email) : "",
                    };
                    generateInvoice(updatedOrder, userDetails, "save");
                  } catch (e) {
                    console.error("Error auto-opening invoice", e);
                  }

                  setShowSuccessModal(true);
                }
              } catch (err) {
                console.error("Payment verification failed", err);
              }
            },
            prefill: {
              name: user?.fullName ? decryptData(user.fullName) : "Game User",
              email: user?.email ? decryptData(user.email) : "user@example.com",
            },
            theme: { color: "#7c3aed" },
          };
          const rzp = new window.Razorpay(options);
          rzp.open();
        }
      } else {
        const response = await axiosInstance.post("/order/stripe/create-payment-intent", {
          amount: totalAmount,
        });

        if (response.data.success) {
          setClientSecret(response.data.clientSecret);
          setOrderData({ items: checkoutItems, amount: totalAmount });
          setOpenStripeModal(true);
        }
      }
    } catch (err) {
      console.error("Error purchasing bundle:", err);
      toast.error("Checkout error. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCloseStripeModal = () => {
    setOpenStripeModal(false);
    setClientSecret("");
  };

  return (
    <BackgroundColor className="relative overflow-x-hidden min-h-screen">
      <SEOHead
        title="Game Bundles – Save Big on Top Game Packs"
        description="Get exclusive game bundles on Yoyo Game. Buy 3 top games in one pack at a special discounted price. Available for PC, Android, iOS, PlayStation, Xbox, and more."
        keywords="game bundles, cheap game packs, buy games bundle, PC game deals, console game bundle, Yoyo Game bundles"
        url="https://yoyokhel.com/bundles"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Game Bundles – Yoyo Game",
          "description": "Exclusive game bundles at unbeatable prices. Buy 3 top titles together and save massively.",
          "url": "https://yoyokhel.com/bundles",
          "isPartOf": { "@type": "WebSite", "name": "Yoyo Game", "url": "https://yoyokhel.com" }
        }}
      />
      {/* Decorative background glows */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-purple-900/10 blur-[150px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-900/10 blur-[150px] pointer-events-none" />

      <div className="pt-28 pb-20 w-full max-w-[1280px] mx-auto px-4 sm:px-6 relative z-10">

        {/* Banner Redesign */}
        <div className="relative mb-16 text-center rounded-[32px] overflow-hidden bg-gradient-to-br from-[#12121c]/90 to-[#1b122c]/90 border border-white/5 p-8 sm:p-14 shadow-[0_24px_50px_rgba(0,0,0,0.4)]">
          <div className="absolute top-0 right-0 w-80 h-80 bg-fuchsia-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/10 rounded-full blur-[100px]" />

          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold tracking-wider text-purple-300 uppercase rounded-full bg-purple-500/10 border border-purple-500/20">
              <FaTags className="text-[10px]" /> Exclusive Packs
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tight text-white leading-none mt-2">
            Game <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-indigo-400 bg-clip-text text-transparent"> Bundles</span>
          </h1>
          <p className="text-gray-400 mt-4 text-base sm:text-lg max-w-2xl mx-auto font-medium">
            Acquire 3 sensational titles for one unbeatable price. Enrich your gaming library while saving massive value!
          </p>
        </div>

        {/* Public Bundles Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 space-y-4">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-400 text-sm font-semibold">Loading available deals...</span>
          </div>
        ) : !bundles || bundles.length === 0 ? (
          <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-12 text-center min-h-[350px] flex flex-col justify-center items-center backdrop-blur-md">
            <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center text-purple-400 text-3xl mb-4 border border-purple-500/20">
              🎮
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">No Active Bundles</h3>
            <p className="text-gray-400 max-w-sm">We are preparing fresh gaming packages. Check back shortly!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {bundles.filter(b => b.active).map((bundle) => (
              <div
                key={bundle._id}
                className="group relative bg-[#12121a]/80 border border-white/5 rounded-[2.5rem] p-6 flex flex-col justify-between hover:border-purple-500/30 hover:shadow-[0_0_40px_rgba(168,85,247,0.1)] transition-all duration-500 backdrop-blur-xl"
              >
                <div>
                  <h3 className="text-2xl font-extrabold text-white mb-2 group-hover:text-purple-300 transition-colors">
                    {bundle.name}
                  </h3>
                  <p className="text-gray-400 text-xs mb-6 line-clamp-2 leading-relaxed">
                    {bundle.description || "A custom curated package of top titles."}
                  </p>

                  {/* Redesigned 3 Games Visual List */}
                  <div className="grid grid-cols-3 gap-3 mb-6 relative">
                    {bundle.games.map((game, idx) => {
                      const availablePlats = Object.keys(game.platforms || {}).filter(
                        (k) => game.platforms[k]?.available
                      );

                      return (
                        <div
                          key={game._id}
                          className="text-center group/game cursor-pointer flex flex-col items-center"
                          onClick={() => navigate(`/gamedetails/${game._id}`)}
                        >
                          <div className="aspect-[3/4] w-full rounded-2xl overflow-hidden border border-white/5 group-hover/game:border-purple-500/50 shadow-lg group-hover/game:shadow-purple-500/10 transition-all duration-300 relative">
                            <img
                              src={game.cover_image?.url || "placeholder.png"}
                              alt={game.title}
                              className="w-full h-full object-cover group-hover/game:scale-105 transition-all duration-500"
                            />
                            {/* Overlay Badge for Game Index */}
                            <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-lg bg-black/75 backdrop-blur-md border border-white/10 flex items-center justify-center text-[10px] font-black text-purple-400">
                              {idx + 1}
                            </div>
                          </div>

                          <p className="text-white/80 text-[11px] font-bold mt-2.5 truncate w-full px-0.5">
                            {game.title}
                          </p>

                          {/* Mini platform badges on game */}
                          <div className="flex items-center gap-1 mt-1 justify-center max-w-full overflow-hidden">
                            {availablePlats.slice(0, 3).map((plat) => (
                              <span key={plat} className="text-[10px]" title={plat}>
                                {getPlatformIcon(plat)}
                              </span>
                            ))}
                            {availablePlats.length > 3 && (
                              <span className="text-[8px] text-gray-500 font-bold">+{availablePlats.length - 3}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {(() => {
                  const totalOriginalPrice = bundle.games.reduce((sum, game) => {
                    const prices = [];
                    if (game.platforms) {
                      Object.keys(game.platforms).forEach((p) => {
                        if (game.platforms[p]?.available && game.platforms[p]?.price > 0) {
                          prices.push(game.platforms[p].price);
                        }
                      });
                    }
                    const best = prices.length > 0 ? Math.min(...prices) : 0;
                    return sum + best;
                  }, 0);
                  const saving = totalOriginalPrice - bundle.price;
                  const discountPercentage = totalOriginalPrice > bundle.price ? Math.round((saving / totalOriginalPrice) * 100) : 0;

                  return (
                    <div className="mt-4 pt-5 border-t border-white/5 flex items-center justify-between">
                      <div>
                        {totalOriginalPrice > bundle.price && (
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-gray-500 text-xs line-through font-medium">${totalOriginalPrice.toFixed(2)}</span>
                            <span className="text-[9px] bg-green-500/10 border border-green-500/25 text-green-400 font-black px-1.5 py-0.5 rounded-md">
                              {discountPercentage}% OFF
                            </span>
                          </div>
                        )}
                        <span className="text-gray-400 text-[10px] block font-bold uppercase tracking-wider">Special Price</span>
                        <span className="text-2xl font-black text-purple-400">${bundle.price.toFixed(2)}</span>
                      </div>
                      <button
                        onClick={() => initiateBundlePurchase(bundle)}
                        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-extrabold rounded-2xl text-xs sm:text-sm transition-all hover:shadow-[0_0_20px_rgba(168,85,247,0.35)] shadow-lg active:scale-95"
                      >
                        Buy Bundle
                      </button>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Redesigned Platform Selection Modal */}
      <Modal open={platformModalOpen} onClose={() => setPlatformModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "520px" },
            bgcolor: "#0b0b11",
            border: "1px solid rgba(255, 255, 255, 0.05)",
            borderRadius: "28px",
            boxShadow: "0 24px 64px rgba(0, 0, 0, 0.75)",
            p: { xs: 3, sm: 4 },
            outline: "none",
          }}
        >
          <div className="text-white">
            <h2 className="text-2xl font-black mb-1 bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
              Customize Platforms
            </h2>
            <p className="text-gray-400 text-xs mb-6">
              Pick your preferred platform key for each title before checking out:
            </p>

            <div className="space-y-4 mb-8">
              {currentBundle?.games.map((g) => {
                const platforms = Object.keys(g.platforms || {}).filter(
                  (key) => g.platforms[key]?.available
                );
                return (
                  <div key={g._id} className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                    <span className="font-bold text-sm text-gray-200 block mb-2.5 truncate">{g.title}</span>
                    <div className="flex flex-wrap gap-2">
                      {platforms.map((p) => {
                        const isSelected = selectedPlatforms[g._id] === p;
                        return (
                          <button
                            key={p}
                            type="button"
                            onClick={() => handlePlatformChange(g._id, p)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all border ${isSelected
                              ? "bg-purple-600/20 border-purple-500 text-purple-200 shadow-[0_0_12px_rgba(168,85,247,0.15)]"
                              : "bg-black/40 border-white/5 hover:border-white/10 text-gray-400 hover:text-white"
                              }`}
                          >
                            {getPlatformIcon(p)}
                            <span className="capitalize">{p}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setPlatformModalOpen(false)}
                className="flex-1 py-3.5 bg-white/5 hover:bg-white/10 border border-white/5 rounded-2xl text-xs font-bold transition-all text-gray-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPurchase}
                className="flex-[1.5] py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-2xl text-xs font-extrabold shadow-lg shadow-purple-650/20 hover:shadow-purple-650/30 transition-all text-white active:scale-95"
              >
                Confirm & Pay
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Stripe Payment Modal */}
      <Modal open={openStripeModal} onClose={handleCloseStripeModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "500px" },
            bgcolor: "#0b0b11",
            border: "1px solid rgba(255, 255, 255, 0.05)",
            borderRadius: "24px",
            boxShadow: "0 24px 64px rgba(0, 0, 0, 0.75)",
            p: 4,
            outline: "none",
          }}
        >
          <div className="text-white">
            <h2 className="text-2xl font-black mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Complete Payment
            </h2>
            {clientSecret && orderData && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <StripeCheckoutForm
                  clientSecret={clientSecret}
                  orderData={orderData}
                  onSuccess={() => {
                    handleCloseStripeModal();
                    dispatch(fetchCart());
                    setShowSuccessModal(true);
                  }}
                  onCancel={handleCloseStripeModal}
                />
              </Elements>
            )}
          </div>
        </Box>
      </Modal>

      <OrderSuccessModal open={showSuccessModal} onClose={() => setShowSuccessModal(false)} />
    </BackgroundColor>
  );
}
